/**
 * Student-facing background options (docs/ROADMAP.md A1).
 * The ids are the values stored in `speaker.dialect_l1` / `training_level`;
 * the labels and hints are what the student reads.
 */
import { DIALECT_GROUPS as SHARED_DIALECT_GROUPS } from "@xinghai/shared";
import type { DialectGroup as DialectGroupId } from "@xinghai/shared";

export type { DialectGroupId };

/** The legal entity behind the platform (consent agreement, footers). */
export const ORG_NAME = "广州叽喳科技有限公司";
export const ORG_SHORT = "叽喳科技";
/** Additional beneficiary of the recording / annotation licences. */
export const BENEFICIARY_NAME = "杨爽";
/** How the two are referred to together in the agreements. */
export const GRANTEES_LABEL = "被授权方";
/** The partner running the course this corpus is collected in. */
export const PARTNER_NAME = "星海音乐学院《歌唱语言艺术》课程团队";
/** Shown in page footers. */
export const COPYRIGHT_LINE = `© 2026 ${ORG_NAME} · 星海正音`;

export interface DialectGroup {
  /** One of the shared `DIALECT_GROUPS` values — the server validates against it. */
  id: DialectGroupId;
  label: string;
  /** Where it is spoken — students pick by place, not by linguistics. */
  hint: string;
}

/**
 * Wording for each shared group. Typed as a total `Record`, so adding or
 * renaming a group in `@xinghai/shared` breaks the build here instead of
 * silently sending an id the server rejects with a bare 422.
 */
const DIALECT_COPY: Record<DialectGroupId, { label: string; hint: string }> = {
  北方官话: { label: "北方官话", hint: "东北、华北、山东、河南、江淮一带" },
  西南官话: { label: "西南官话", hint: "四川、重庆、云南、贵州、湖北大部" },
  吴语: { label: "吴语", hint: "上海、苏南、浙江大部" },
  粤语: { label: "粤语", hint: "广州、珠三角、广西部分、港澳" },
  闽语: { label: "闽语", hint: "福建、台湾、海南（潮汕单列）" },
  潮汕: { label: "潮汕话", hint: "汕头、潮州、揭阳一带" },
  客家: { label: "客家话", hint: "梅州、粤东、闽西、赣南一带" },
  湘赣: { label: "湘语 / 赣语", hint: "湖南、江西一带" },
};

/** The eight groups the dispatch rules (方言先验, spec §2.4) are built on. */
export const DIALECT_GROUPS: readonly DialectGroup[] = SHARED_DIALECT_GROUPS.map((id) => ({
  id,
  ...DIALECT_COPY[id],
}));

/** The exact ids the API accepts. */
export const DIALECT_IDS: readonly DialectGroupId[] = SHARED_DIALECT_GROUPS;

export const DIALECT_LABELS: Record<string, string> = Object.fromEntries(
  DIALECT_GROUPS.map((d) => [d.id, d.label]),
);

export type TrainingLevel = "vocal" | "non_vocal" | "freshman";

export const TRAINING_LEVELS: ReadonlyArray<{ value: TrainingLevel; label: string; hint: string }> =
  [
    { value: "vocal", label: "声乐 / 播音专业", hint: "受过系统的发声或播音训练" },
    { value: "non_vocal", label: "非声乐专业", hint: "没有受过系统训练" },
    { value: "freshman", label: "刚入学", hint: "刚开始接触这门课" },
  ];

export const TRAINING_LEVEL_LABELS: Record<string, string> = Object.fromEntries(
  TRAINING_LEVELS.map((t) => [t.value, t.label]),
);

export type Gender = "F" | "M" | "other";

export const GENDERS: ReadonlyArray<{ value: Gender; label: string }> = [
  { value: "F", label: "女" },
  { value: "M", label: "男" },
  { value: "other", label: "其他 / 不想说" },
];

/** Birth years offered in the register form (oldest first is unhelpful on mobile). */
export function birthYearOptions(now = new Date().getFullYear()): number[] {
  const years: number[] = [];
  for (let y = now - 14; y >= now - 60; y -= 1) years.push(y);
  return years;
}

/** 红线 14.6 — under 18 needs a guardian co-signature on the consent page. */
export function isMinorByBirthYear(birthYear: number, now = new Date().getFullYear()): boolean {
  return now - birthYear < 18;
}

/** docs/ROADMAP.md A2 — 240 单字 are presented as 8 关 of 30. */
export const LEVEL_SIZE = 30;
